import { model as router } from '!/router'
import { model as config, remote } from '@/config'
import { MediaUrl } from '@/types'
import { api, EpgInfo } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  EventCallable,
  sample,
  split,
  Store,
} from 'effector'
import { or } from 'patronum'
import { epgDatesModel } from '~/entities/epg/dates'
import { model as session } from '~/entities/session'
import { activeProgramModel } from '~/features/epg/active-program'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { selectEpgDateModel } from '~/features/epg/dates'
import { epgFeaturePlayerModel } from '~/features/epg/player'
import {
  LiveProgramInfo,
  model as loadEpgProgramsModel,
} from '~/features/epg/programs'
import { Selected } from '~/features/epg/programs/select'
import { googlePalFeatureModel } from '~/features/google-pal'
import { $route } from '~/processes/navigation'
import {
  contentTypesWithEpg,
  ONE_MINUTE_IN_SEC,
  ONE_SECOND_MS,
  PATH,
  ROUTE_NAME,
} from '~/shared/constants'
import {
  exists,
  formatChannelToInfoPopupData,
  getMacrosHeader,
  T,
} from '~/shared/helpers'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import { $watchId, generateWatchIdFx } from '~/widgets/player/analytics'
import { epgCategoriesWidgetModel } from '../categories'
import { $activeChannelWithFormattedEPG, $dayTabs } from './epg'
import { getCurrentChannelInfo } from './lib'

// init player
export const init = createEvent()

// reset widget on sign out
export const reset = createEvent()

export const loadChannelUrl = createEvent<{
  id: number
  startTime?: number
  rewind?: number
  isRewind?: boolean
}>()

export const setCatchupTimeInterval = createEvent<{
  startTime: number
  endTime: number
} | null>()

export const resetCatchupTimeInterval = createEvent()
export const setShouldResetCatchupTimeInterval = createEvent()
export const resetShouldResetCatchupTimeInterval = createEvent()
export const $shouldResetCatchupTimeInterval = createStore(false)
  .on(setShouldResetCatchupTimeInterval, () => true)
  .reset(resetShouldResetCatchupTimeInterval)

export const $catchupInterval = createStore<{
  startTime: number
  endTime: number
} | null>(null)
  .on(setCatchupTimeInterval, (_, timeInterval) => timeInterval)
  .reset(resetCatchupTimeInterval)

export const setTimeshift = createEvent<{
  enabled: boolean
  shift: number
} | null>()
export const resetTimeshift = createEvent()
export const $timeshift = createStore<{
  enabled: boolean
  shift: number
} | null>(null)
  .on(setTimeshift, (_, timeshift) => {
    if (!timeshift) return null

    const { enabled, shift } = timeshift
    const userTimezoneOffset = -new Date().getTimezoneOffset()

    const timeshiftTime = (shift - userTimezoneOffset) * ONE_MINUTE_IN_SEC

    if (!enabled) return { ...timeshift, shift: 0 }

    return { ...timeshift, shift: timeshiftTime }
  })
  .reset(resetTimeshift)

sample({
  clock: selectEpgChannelModel.$active,
  filter: (channel) => Boolean(channel),
  fn: (channel) => channel?.timeshift || null,
  target: setTimeshift,
})

export const getOnePlaybackUrlFx: typeof api.live.base.getOnePlaybackUrlFx =
  attach({
    effect: api.live.base.getOnePlaybackUrlFx,
  })

export const getCatchupPlaybackUrlFx: typeof api.live.base.getCatchupPlaybackUrlFx =
  attach({
    effect: api.live.base.getCatchupPlaybackUrlFx,
  })

// currently selected channel url
export const $url = createStore<MediaUrl | null>(null)
  .reset(
    // Resetting url ensures the play_stop analytics event is sent before receiving and playing the new URL
    getOnePlaybackUrlFx.fail,
    getCatchupPlaybackUrlFx,
    getCatchupPlaybackUrlFx.fail,
    loadChannelUrl,
    selectEpgChannelModel.$activeId
  )
  .on(
    [getOnePlaybackUrlFx.doneData, getCatchupPlaybackUrlFx.doneData],
    (_, { playbackUrl }) =>
      playbackUrl
        ? {
            playbackUrl,
          }
        : null
  )

export const $isUrlRequestPending: Store<boolean> = or(
  getOnePlaybackUrlFx.pending,
  getCatchupPlaybackUrlFx.pending
)

export const $isUrlRequestFailed: Store<boolean> = createStore(false)
  .on([getOnePlaybackUrlFx.fail, getCatchupPlaybackUrlFx.fail], T)
  .reset([
    getCatchupPlaybackUrlFx,
    loadChannelUrl,
    selectEpgChannelModel.$activeId,
  ])

const resetLoadChannelUrlParams = createEvent()
const $loadChannelUrlParams = createStore<{
  id: number
  startTime?: number
  rewind?: number
  isRewind?: boolean
} | null>(null)
  .on(loadChannelUrl, (_, data) => data)
  .reset(resetLoadChannelUrlParams)

// get rewind url
sample({
  clock: getOnePlaybackUrlFx.doneData,
  source: {
    requested: $loadChannelUrlParams,
    activeChannel: selectEpgChannelModel.$active,
  },
  fn: ({ requested, activeChannel }, url) => {
    if (
      exists(requested?.startTime) &&
      requested.isRewind &&
      activeChannel?.id === requested.id
    ) {
      return {
        ...url,
        timeShiftSec: Math.round(requested.startTime / ONE_SECOND_MS),
      }
    }
    return url
  },
  target: [$url, resetLoadChannelUrlParams],
})

export const resetIsCatchupUrlReceived = createEvent()
export const $isCatchupUrlReceived = createStore(false)
  .on(getCatchupPlaybackUrlFx.finally, () => true)
  .reset(resetIsCatchupUrlReceived)

export const $channelInfo = combine(
  {
    pathname: router.$pathname,
    region: epgFeaturePlayerModel.$playlistRegion,
    channel: selectEpgChannelModel.$active,
    channelsList: epgFeaturePlayerModel.$playerPlaylist,
    categories: epgCategoriesWidgetModel.$filteredCategories,
  },
  ({ pathname, region, channel, channelsList, categories }) => {
    return getCurrentChannelInfo({
      pathname: pathname ?? '',
      region,
      channelId: channel?.id,
      channels: channelsList,
      categories,
    })
  }
)

// Reset idType store on every active channel update, catchup interval change, or rewind url load
sample({
  clock: [selectEpgChannelModel.$activeId, $catchupInterval, loadChannelUrl],
  source: {
    id: selectEpgChannelModel.$activeId,
  },
  filter: ({ id }) => Boolean(id),
  fn: ({ id }) => {
    return {
      id,
      type: contentTypesWithEpg.epg,
    }
  },
  // as id could be from the same channel (but different time interval), we need to reset id type
  target: [
    googlePalFeatureModel.resetIdType,
    googlePalFeatureModel.checkIfNeedToLoadNonce,
  ],
})

export const doRequest: EventCallable<void> = createEvent<void>()

// Load content after watchId is generated
sample({
  clock: generateWatchIdFx.done,
  source: {
    idType: googlePalFeatureModel.$idType,
  },
  filter: ({ idType }) => idType?.type === contentTypesWithEpg.epg,
  target: doRequest,
})

export const getLiveUrl = createEvent()
export const getCatchupUrl = createEvent()

split({
  clock: doRequest,
  source: $catchupInterval,
  match: {
    getLiveUrl: (catchupInterval) => !catchupInterval,
    getCatchupUrl: (catchupInterval) => Boolean(catchupInterval),
  },
  cases: {
    getLiveUrl,
    getCatchupUrl,
  },
})

sample({
  clock: getLiveUrl,
  source: {
    idType: googlePalFeatureModel.$idType,
    nonce: googlePalFeatureModel.$nonce,
    macrosHeader: config.get(remote.uvo_macrosHeader),
    requestRandom: $watchId,
    loadChannelUrlParams: $loadChannelUrlParams,
    timeshift: $timeshift,
    sessionInfo: session.$info,
  },
  fn: ({
    idType,
    nonce,
    macrosHeader,
    requestRandom,
    loadChannelUrlParams,
    timeshift,
    sessionInfo,
  }) => {
    const userGuid = sessionInfo?.userGuid
    let headers = {}
    if (macrosHeader && requestRandom && userGuid) {
      headers = {
        [macrosHeader]: getMacrosHeader(requestRandom, userGuid, nonce),
      }
    }
    const isRewind = loadChannelUrlParams?.isRewind

    if (isRewind) {
      const startTime = loadChannelUrlParams.startTime
      const rewind = loadChannelUrlParams.rewind
      return { id: idType?.id, headers, startTime, rewind, isRewind }
    }

    const isTimeshift = timeshift?.enabled

    if (isTimeshift) {
      return { id: idType?.id, headers, timeshift: timeshift?.shift }
    }

    return { id: idType?.id, headers }
  },
  target: getOnePlaybackUrlFx,
})

sample({
  clock: getCatchupUrl,
  source: {
    idType: googlePalFeatureModel.$idType,
    nonce: googlePalFeatureModel.$nonce,
    macrosHeader: config.get(remote.uvo_macrosHeader),
    catchupInterval: $catchupInterval,
    requestRandom: $watchId,
    sessionInfo: session.$info,
  },
  fn: ({
    idType,
    nonce,
    macrosHeader,
    catchupInterval,
    requestRandom,
    sessionInfo,
  }) => {
    const userGuid = sessionInfo?.userGuid
    let headers = {}
    if (macrosHeader && requestRandom && userGuid) {
      headers = {
        [macrosHeader]: getMacrosHeader(requestRandom, userGuid, nonce),
      }
    }
    const { startTime, endTime } = catchupInterval || {}
    return { id: idType?.id, headers, startTime, endTime }
  },
  target: getCatchupPlaybackUrlFx,
})

// Filter to check if we're on the EPG page or player page
const $shouldBeLoadedEpgPrograms = $route.map((route) => {
  const routeName = route?.routeName ?? ''
  return (
    routeName === ROUTE_NAME.LIVE_CATEGORIES ||
    routeName === `${PATH.LIVE}${PATH.PLAY}`
  )
})

// Currently we can use for epg-board page loaded programs for epg in player
// (the requested range of days in the player covers the small range on the epg-board)
sample({
  clock: [init, epgChannelsWidgetModel.$activeList, epgDatesModel.$today],
  source: {
    channels: epgChannelsWidgetModel.$activeList,
    date: epgDatesModel.$today,
    shouldBeLoadedEpgPrograms: $shouldBeLoadedEpgPrograms,
  },
  filter: ({ channels, date, shouldBeLoadedEpgPrograms }) => {
    return date != null && channels.length > 0 && shouldBeLoadedEpgPrograms
  },
  fn: ({ channels, date }) => {
    return { channels, date }
  },
  target: loadEpgProgramsModel.load,
})

// Load channel`s programs if it is not in active channels list (f.e. navigate by direct link)
// for program info in program description block or sidebar
sample({
  clock: [epgChannelsWidgetModel.$selected, epgDatesModel.$today],
  source: {
    activeChannel: epgChannelsWidgetModel.$selected,
    date: epgDatesModel.$today,
    shouldBeLoadedEpgPrograms: $shouldBeLoadedEpgPrograms,
  },
  filter: ({ activeChannel, date, shouldBeLoadedEpgPrograms }) => {
    return date != null && activeChannel != null && shouldBeLoadedEpgPrograms
  },
  fn: ({ activeChannel, date }) => {
    return { channels: [activeChannel], date }
  },
  target: loadEpgProgramsModel.load,
})

export const $playerInfoPopupData = selectEpgChannelModel.$active.map(
  (channel) => formatChannelToInfoPopupData(channel)
)

export const selectProgramToPlay = createEvent<
  LiveProgramInfo | Selected | EpgInfo | null
>()

sample({
  clock: selectProgramToPlay,
  source: $timeshift,
  fn: (timeshift, selectedProgram) => {
    if (selectedProgram?.isLive) {
      return null
    }

    const startTime =
      (selectedProgram?.catchUp?.catchupInterval?.startTime || 0) -
      (timeshift?.shift || 0) * ONE_SECOND_MS
    const endTime =
      (selectedProgram?.catchUp?.catchupInterval?.endTime || 0) -
      (timeshift?.shift || 0) * ONE_SECOND_MS

    if (startTime && endTime) {
      return {
        startTime,
        endTime,
      }
    }

    return null
  },
  target: setCatchupTimeInterval,
})

sample({
  clock: $activeChannelWithFormattedEPG,
  target: activeProgramModel.$epgActiveChannelWithFormattedEPG,
})

sample({
  clock: $catchupInterval,
  target: activeProgramModel.$epgCatchupInterval,
})

sample({
  clock: $timeshift,
  target: activeProgramModel.$epgTimeshift,
})

sample({
  clock: $url,
  target: activeProgramModel.$epgUrl,
})

sample({
  clock: resetCatchupTimeInterval,
  target: activeProgramModel.resetEpgPlayingRewindData,
})

// set active day for sidebar in player if change channel or any available
// to be selected program from active channel (change catchup interval)
sample({
  clock: [selectEpgChannelModel.$activeId, $catchupInterval],
  source: {
    dayTabs: $dayTabs,
    catchupInterval: $catchupInterval,
  },
  filter: ({ dayTabs }) => Boolean(dayTabs) && Boolean(dayTabs?.length),
  fn: ({ dayTabs, catchupInterval }) => {
    // if catchup interval is set, we need to find day with that interval
    if (catchupInterval) {
      const start = catchupInterval.startTime
      const day = dayTabs?.find((item) => {
        const dayStart = new Date(item.year, item.month, item.day).getTime()
        const dayEnd = new Date(item.year, item.month, item.day + 1).getTime()
        return start >= dayStart && start < dayEnd
      })
      return day
    }

    // if catchup interval is not set, it means that we are in live mode, so we need to find today day
    const today = dayTabs?.find((item) => item.isToday)
    return today
  },
  target: selectEpgDateModel.setActive,
})
